import { useLayoutEffect, useRef } from "react";

import type { PixelArt } from "../domain/canvas-model";

function hexToRgba(hex: string): [number, number, number, number] {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) || 0;
  const g = parseInt(h.slice(2, 4), 16) || 0;
  const b = parseInt(h.slice(4, 6), 16) || 0;
  const a = h.length >= 8 ? (parseInt(h.slice(6, 8), 16) || 0) : 255;
  return [r, g, b, a];
}

/**
 * Shared PIXEL ART renderer (editor + public Grid). 1 cell == 1 world/parcel
 * unit, so the W×H matrix maps straight onto the element box; drawn to a tiny
 * W×H <canvas> and CSS-scaled with `image-rendering: pixelated` for crisp pixels
 * at any zoom. Index 0 = transparent (the territory shows through).
 */
export function PixelArtRender({ pixelArt, boxW, boxH }: { pixelArt: PixelArt; boxW: number; boxH: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { w, h, palette, rle } = pixelArt;
  useLayoutEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(w, h);
    const rgba = palette.map(hexToRgba);
    let p = 0;
    for (const [idx, count] of rle) {
      const col = idx === 0 ? null : rgba[idx - 1];
      for (let k = 0; k < count && p < w * h; k++, p++) {
        const o = p * 4;
        if (col) { img.data[o] = col[0]; img.data[o + 1] = col[1]; img.data[o + 2] = col[2]; img.data[o + 3] = col[3]; }
        else { img.data[o + 3] = 0; }
      }
    }
    ctx.putImageData(img, 0, 0);
  }, [w, h, palette, rle]);
  return (
    <canvas ref={ref} width={w} height={h} data-testid="pixel-art-canvas"
      style={{ width: Math.max(1, boxW), height: Math.max(1, boxH), display: "block", imageRendering: "pixelated" }} />
  );
}
